import { useState } from "react";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { HomeDepotInterstitialDisplay } from "commonComponents/entity/vendor/HomeDepotInterstitialDisplay/HomeDepotInterstitialDisplay";
import HomeDepotProXtraLogo from "commonComponents/entity/vendor/HomeDepotLogo/HomeDepotProXtraLogo";
import { HomeDepotUnconnectedDisplay } from "commonComponents/entity/vendor/HomeDepotUnconnectedDisplay/HomeDepotUnconnectedDisplay";
import { DateDisplay } from "commonComponents/utilities/DateDisplay/DateDisplay";
import { NoteText } from "commonComponents/utilities/NoteText/NoteText";
import { PageSection } from "commonComponents/utilities/PageSection/PageSection";
import { StatusTagDisplay } from "commonComponents/utilities/Status/StatusTagDisplay";

import { type VendorAccountUserDetails } from "entity/internalUser/InternalUserDetails/InternalUser.api.types";
import { type HomeDepotSettingsFormActions } from "entity/vendor/HomeDepotSettings/HomeDepotSettings.api.types";

const marketingTitle = "Make purchasing supplies quick and easy";
const marketingDetails =
    "Once connected to your The Home Depot Pro Xtra account, you can easily send items directly from Buildertrend to your online shopping cart at The Home Depot for quicker purchasing.";

interface IInternalUserVendorConnectedProps {
    vendorAccountUserDetails: VendorAccountUserDetails;
    actionBeingPerformed: HomeDepotSettingsFormActions;
    onDisconnect: () => void;
}

const HomeDepotConnected: React.FunctionComponent<IInternalUserVendorConnectedProps> = ({
    vendorAccountUserDetails: vendor,
    actionBeingPerformed,
    onDisconnect,
}) => {
    return (
        <>
            <BTRow>
                <BTCol xs={24}>
                    <HomeDepotProXtraLogo height={56} />
                </BTCol>
            </BTRow>
            <BTRow className="margin-vertical-lg">
                <BTCol xs={24}>
                    <StatusTagDisplay statusType="success" statusText="Connected" />
                    <NoteText>
                        <span className="margin-left-sm">
                            Connected since <DateDisplay value={vendor.connectionDate!} showTime />
                        </span>
                    </NoteText>
                </BTCol>
            </BTRow>
            {!vendor.isReadOnly && (
                <BTRow>
                    <BTCol xs={24}>
                        <BTButtonDeprecated
                            data-testid="disconnectHomeDepot"
                            actionBeingPerformed={actionBeingPerformed}
                            onClick={onDisconnect}
                        >
                            Disconnect
                        </BTButtonDeprecated>
                    </BTCol>
                </BTRow>
            )}
        </>
    );
};

interface IInternalUserVendorTabProps extends IInternalUserVendorConnectedProps {
    onInitiateConnection: () => void;
    firstName: string;
    lastName: string;
}

const InternalUserVendorTab: React.FunctionComponent<IInternalUserVendorTabProps> = ({
    vendorAccountUserDetails,
    actionBeingPerformed,
    onInitiateConnection,
    onDisconnect,
    firstName,
    lastName,
}) => {
    const [shouldShowInterstitialPage, setShouldShowInterstitialPage] = useState(false);
    const readOnlyDetails = (
        <>
            You do not have access to connect {firstName} {lastName} to The Home Depot. {firstName}{" "}
            {lastName} can connect their own Buildertrend account to The Home Depot through their
            user preferences.
        </>
    );
    const details = !vendorAccountUserDetails.canConnect ? readOnlyDetails : marketingDetails;
    return (
        <PageSection title="The Home Depot">
            {!vendorAccountUserDetails.isConnected && !shouldShowInterstitialPage && (
                <HomeDepotUnconnectedDisplay
                    title={marketingTitle}
                    details={details}
                    showLearnMoreLink={false}
                    showConnectButton={vendorAccountUserDetails.canConnect}
                    onClick={() => {
                        setShouldShowInterstitialPage(true);
                    }}
                />
            )}
            {shouldShowInterstitialPage && (
                <HomeDepotInterstitialDisplay
                    actionBeingPerformed={actionBeingPerformed}
                    onClick={onInitiateConnection}
                />
            )}
            {vendorAccountUserDetails.isConnected && (
                <HomeDepotConnected
                    vendorAccountUserDetails={vendorAccountUserDetails}
                    actionBeingPerformed={actionBeingPerformed}
                    onDisconnect={onDisconnect}
                />
            )}
        </PageSection>
    );
};

export default InternalUserVendorTab;
